import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Story } from '@/utils/storiesData';
import { Content } from '@/types/content';
import { useQuery } from '@tanstack/react-query';
import contentfulVideoService from '@/services/contentfulVideoService';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';

interface MagazineHeroProps {
  stories: Story[];
}

const MagazineHero: React.FC<MagazineHeroProps> = ({
  stories
}) => {
  const navigate = useNavigate();
  const {
    resolvedTheme
  } = useTheme();
  const isMobile = useIsMobile();
  const {
    data: videos = []
  } = useQuery({
    queryKey: ['videos'],
    queryFn: contentfulVideoService.getVideos
  });
  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';

  // Get one story from each category for rotating hero
  const getRandomStoryFromCategories = (stories: Story[]) => {
    const storyCategories = stories.reduce((acc, story) => {
      if (!acc[story.category]) {
        acc[story.category] = [];
      }
      acc[story.category].push(story);
      return acc;
    }, {} as Record<string, Story[]>);

    // Get one random story from each category
    const categoryStories = Object.values(storyCategories).map(categoryStories => {
      const randomIndex = Math.floor(Math.random() * categoryStories.length);
      return categoryStories[randomIndex];
    });

    // Pick a random story from the category stories
    const randomIndex = Math.floor(Math.random() * categoryStories.length);
    return categoryStories[randomIndex];
  };

  // Get rotating hero story (changes on each refresh)
  const mainStory = getRandomStoryFromCategories(stories) || stories[0];

  // Get latest video
  const latestVideo = videos.sort((a, b) => new Date(b.releaseDate || '').getTime() - new Date(a.releaseDate || '').getTime())[0];

  // Get stories by category
  const architectureStories = stories.filter(story => story.category === 'Architecture' || story.tags?.some(tag => tag.toLowerCase().includes('architect')));
  const automotiveStories = stories.filter(story => story.category === 'Automotive' || story.tags?.some(tag => tag.toLowerCase().includes('automotive')));
  const designStories = stories.filter(story => story.category === 'Design' || story.category === 'Interior Design' || story.tags?.some(tag => tag.toLowerCase().includes('design')));
  const europeanStories = stories.filter(story => story.tags?.some(tag => tag.toLowerCase().includes('european')));

  const handleStoryClick = (story: Story) => {
    navigate(`/stories/${story.slug}`);
  };
  const handleVideoClick = (video: Content) => {
    navigate(`/videos/${video.slug || video.id}`);
  };
  const handleCategoryClick = (category: string) => {
    navigate(`/browse?category=${encodeURIComponent(category)}`);
  };

  if (isMobile) {
    return <div className="pb-6 space-y-6">
        {/* Main Story */}
        <div className="px-4">
          {mainStory && <div onClick={() => handleStoryClick(mainStory)} className="cursor-pointer group">
              <div className="aspect-[4/3] bg-muted overflow-hidden">
                {mainStory.coverImage && <img src={mainStory.coverImage} alt={mainStory.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />}
              </div>
              <div className="pt-4">
                <span className={`text-xs font-medium ${mutedTextColor} uppercase tracking-wide`}>
                  {mainStory.category}
                </span>
                <h2 className={`text-xl ${textColor} mt-1 line-clamp-2 font-medium leading-tight`}>
                  {mainStory.title}
                </h2>
              </div>
            </div>}
        </div>

        {/* Horizontal Scrolling Content */}
        <HorizontalScroller showNextCard={true} snap snapAlign="start" snapStopAlways spacing="custom-10px" disableScale>
          {/* Latest Video */}
          {latestVideo && <div onClick={() => handleVideoClick(latestVideo)} className="relative cursor-pointer group flex-shrink-0 w-[70vw]">
              <div className="aspect-video bg-muted overflow-hidden">
                {latestVideo.thumbnail && <img src={latestVideo.thumbnail} alt={latestVideo.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-xs font-medium text-white/80 uppercase tracking-wide">
                  Latest Video
                </span>
                <h3 className="text-sm text-white mt-1 line-clamp-2 font-medium">
                  {latestVideo.title}
                </h3>
              </div>
            </div>}

          {/* Architecture Stories */}
          {architectureStories[0] && <div onClick={() => handleStoryClick(architectureStories[0])} className="relative cursor-pointer group flex-shrink-0 w-[70vw]">
              <div className="aspect-video bg-muted overflow-hidden">
                {architectureStories[0].coverImage && <img src={architectureStories[0].coverImage} alt={architectureStories[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-xs font-medium text-white/80 uppercase tracking-wide">
                  Architecture
                </span>
                <h3 className="text-sm text-white mt-1 line-clamp-2 font-medium">
                  {architectureStories[0].title}
                </h3>
              </div>
            </div>}

          {/* Automotive Stories */}
          {automotiveStories[0] && <div onClick={() => handleStoryClick(automotiveStories[0])} className="relative cursor-pointer group flex-shrink-0 w-[70vw]">
              <div className="aspect-video bg-muted overflow-hidden">
                {automotiveStories[0].coverImage && <img src={automotiveStories[0].coverImage} alt={automotiveStories[0].title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-xs font-medium text-white/80 uppercase tracking-wide">
                  Automotive
                </span>
                <h3 className="text-sm text-white mt-1 line-clamp-2 font-medium">
                  {automotiveStories[0].title}
                </h3>
              </div>
            </div>}
        </HorizontalScroller>
      </div>;
  }
  // Desktop layout inspired by Remote Control Records - centered hero image
  return <div className="w-full min-h-[80vh] flex items-center justify-center bg-background py-[12px]">
      <div className="max-w-5xl xl:max-w-6xl 2xl:max-w-[100.8rem] 3xl:max-w-[115.2rem] 4k:max-w-[120rem] mx-auto px-6 md:px-8 xl:px-12 2xl:px-16 4k:px-20">
        {/* Main centered hero image */}
        {mainStory && <div onClick={() => handleStoryClick(mainStory)} className="relative aspect-[3/2] 2xl:aspect-[5/3] 3xl:aspect-[16/9] overflow-hidden cursor-pointer group mb-8">
            <div className="absolute inset-0 bg-muted">
              {mainStory.coverImage && <img src={mainStory.coverImage} alt={mainStory.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />}
            </div>
            {/* Very subtle overlay like Remote Control */}
            <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors duration-300" />
          </div>}
        
        {/* Content below image - minimal and centered like Remote Control */}
        {mainStory && <div className="text-center max-w-2xl mx-auto">
            <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 block">
              {mainStory.category}
            </span>
            <h1 className="text-2xl md:text-3xl font-chroma text-foreground mb-4 leading-tight">
              {mainStory.title}
            </h1>
            <p className="text-muted-foreground text-base leading-relaxed">
              {mainStory.excerpt}
            </p>
          </div>}
      </div>
    </div>;
};

export default MagazineHero;
